import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import {
  Button,
  Input,
  Text,
  Card,
  Badge,
  SlideOver,
  Table,
} from '@teals/ui-web';
import type { Column } from '@teals/ui-web';
import {
  useGetDataSourcesQuery,
  useCreateDataSourceMutation,
  useDeleteDataSourceMutation,
  useVerifyConnectionMutation,
  useAnalyzeSchemaMutation,
  type DataSource,
  type SchemaField,
  type DsType,
  type CreateDataSourceRequest,
} from '../../store/api/dataSourcesApi';

/* ------------------------------------------------------------------ */
/*  Styled components                                                  */
/* ------------------------------------------------------------------ */

const PageWrapper = styled.div`
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const FilterBadge = styled(Badge)<{ $active?: boolean }>`
  cursor: pointer;
  opacity: ${(p) => (p.$active ? 1 : 0.5)};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const DSCard = styled(Card)`
  cursor: pointer;
  border: 1px solid var(--border);
  transition: box-shadow 0.2s, border-color 0.2s;
  &:hover {
    border-color: var(--accent);
    box-shadow: var(--shadow-md);
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
`;

const DetailSection = styled.div`
  margin-bottom: 1.5rem;
`;

const DetailLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.375rem;
`;

const DetailValue = styled.div`
  font-size: 0.875rem;
  color: var(--text-primary);
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--border);
  margin: 1.5rem 0;
`;

const WizardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const TypeCard = styled(Card)<{ $selected?: boolean }>`
  cursor: pointer;
  border: 2px solid
    ${(p) => (p.$selected ? 'var(--accent)' : 'var(--border)')};
  transition: border-color 0.2s;
  &:hover {
    border-color: var(--accent);
  }
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SelectLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const StyledSelect = styled.select`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--bg-surface);
  color: var(--text-primary);
  font-size: 0.875rem;
  outline: none;
  &:focus {
    border-color: var(--accent);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-muted);
`;

const ErrorBanner = styled.div`
  padding: 0.75rem 1rem;
  background: var(--danger-bg, #fee2e2);
  color: var(--danger, #dc2626);
  border-radius: 8px;
  margin-bottom: 1rem;
`;

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const DS_TYPE_LABELS: Record<DsType, string> = {
  search_index: 'Search Index',
  blob_storage: 'Blob Storage',
  unstructured_store: 'Unstructured',
  byo: 'Bring Your Own',
};

const DS_TYPE_DESCRIPTIONS: Record<DsType, string> = {
  search_index: 'Platform-managed search index with schema definition',
  blob_storage: 'Platform-managed object storage (S3, GCS)',
  unstructured_store: 'Platform-managed unstructured document store',
  byo: 'Connect to your own existing data source',
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const DataSources: React.FC = () => {
  const { data: dataSources, isLoading, error } = useGetDataSourcesQuery();
  const [createDs, { isLoading: isCreating }] = useCreateDataSourceMutation();
  const [deleteDs] = useDeleteDataSourceMutation();
  const [analyzeSchema] = useAnalyzeSchemaMutation();
  const [verifyConn, { isLoading: isVerifying }] =
    useVerifyConnectionMutation();

  const [selected, setSelected] = useState<DataSource | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<DsType | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  // Wizard state
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardType, setWizardType] = useState<DsType>('search_index');
  const [wizardName, setWizardName] = useState('');
  const [wizardDescription, setWizardDescription] = useState('');
  const [wizardProvider, setWizardProvider] = useState('algolia');
  const [wizardAppId, setWizardAppId] = useState('');
  const [wizardApiKey, setWizardApiKey] = useState('');
  const [wizardIndexName, setWizardIndexName] = useState('');
  const [wizardFields, setWizardFields] = useState<SchemaField[]>([]);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState('string');
  const [wizardError, setWizardError] = useState('');
  const [, setVerifyResult] = useState<Record<string, unknown> | null>(null);

  const isByo = wizardType === 'byo';
  const totalSteps = isByo ? 5 : 4;

  const filteredSources = useMemo(() => {
    if (!dataSources) return [];
    return dataSources.filter((ds) => {
      const matchesSearch =
        !search ||
        ds.name.toLowerCase().includes(search.toLowerCase()) ||
        ds.dataSourceId.toLowerCase().includes(search.toLowerCase());
      const matchesType = !typeFilter || ds.dsType === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [dataSources, search, typeFilter]);

  const resetWizard = () => {
    setWizardStep(0);
    setWizardType('search_index');
    setWizardName('');
    setWizardDescription('');
    setWizardProvider('algolia');
    setWizardAppId('');
    setWizardApiKey('');
    setWizardIndexName('');
    setWizardFields([]);
    setNewFieldName('');
    setNewFieldType('string');
    setWizardError('');
    setVerifyResult(null);
  };

  const openWizard = () => {
    resetWizard();
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
    resetWizard();
  };

  const addField = () => {
    if (!newFieldName.trim()) return;
    setWizardFields((prev) => [
      ...prev,
      { name: newFieldName.trim(), type: newFieldType, searchable: false, indexable: true },
    ]);
    setNewFieldName('');
    setNewFieldType('string');
  };

  const removeField = (name: string) => {
    setWizardFields((prev) => prev.filter((f) => f.name !== name));
  };

  const handleAnalyzeSchema = async () => {
    if (wizardFields.length === 0) return;
    try {
      const result = await analyzeSchema({
        fields: wizardFields.map((f) => ({ name: f.name, type: f.type })),
      }).unwrap();
      setWizardFields(result.fields);
    } catch {
      setWizardError('Schema analysis failed');
    }
  };

  const handleVerifyConnection = async () => {
    setWizardError('');
    if (!wizardName.trim()) {
      setWizardError('Name is required');
      return;
    }
    try {
      const req: CreateDataSourceRequest = {
        name: wizardName.trim(),
        ds_type: 'byo',
        description: wizardDescription || undefined,
        provider: wizardProvider,
        connection_config: {
          app_id: wizardAppId,
          api_key: wizardApiKey,
          index_name: wizardIndexName,
        },
      };
      const created = await createDs(req).unwrap();
      const result = await verifyConn(created.dataSourceId).unwrap();
      setVerifyResult(result as unknown as Record<string, unknown>);
      setWizardStep(4);
    } catch (e: unknown) {
      setWizardError(
        typeof e === 'object' && e !== null && 'data' in e
          ? String((e as { data: { message?: string } }).data?.message ?? e)
          : 'Verification failed',
      );
    }
  };

  const handleCreatePlatformManaged = async () => {
    setWizardError('');
    if (!wizardName.trim()) {
      setWizardError('Name is required');
      return;
    }
    try {
      const req: CreateDataSourceRequest = {
        name: wizardName.trim(),
        ds_type: wizardType,
        description: wizardDescription || undefined,
        connection_config: {},
        schema_definition: wizardFields.length
          ? { fields: wizardFields }
          : undefined,
      };
      await createDs(req).unwrap();
      closeWizard();
    } catch (e: unknown) {
      setWizardError(
        typeof e === 'object' && e !== null && 'data' in e
          ? String((e as { data: { message?: string } }).data?.message ?? e)
          : 'Creation failed',
      );
    }
  };

  const handleDelete = async (dsId: string) => {
    try {
      await deleteDs(dsId).unwrap();
      setDeleteConfirm(null);
      if (selected?.dataSourceId === dsId) setSelected(null);
    } catch {
      /* handled by RTK error state */
    }
  };

  /* -------------------------------------------------------------- */
  /*  Wizard step renderer                                           */
  /* -------------------------------------------------------------- */

  const renderWizardStep = () => {
    if (wizardStep === 0) {
      return (
        <StepContent>
          <Text variant="h4">Select Data Source Type</Text>
          {(Object.keys(DS_TYPE_LABELS) as DsType[]).map((t) => (
            <TypeCard
              key={t}
              padding={16}
              $selected={wizardType === t}
              onClick={() => setWizardType(t)}
            >
              <Text variant="h5">{DS_TYPE_LABELS[t]}</Text>
              <Text variant="body1" style={{ color: 'var(--text-muted)', marginTop: 4 }}>
                {DS_TYPE_DESCRIPTIONS[t]}
              </Text>
            </TypeCard>
          ))}
        </StepContent>
      );
    }

    if (!isByo) {
      if (wizardStep === 1) {
        return (
          <StepContent>
            <Text variant="h4">Define Schema</Text>
            <Input
              label="Name"
              value={wizardName}
              onChange={(e) => setWizardName(e.target.value)}
              placeholder="e.g. Product Catalog"
            />
            <Input
              label="Description"
              value={wizardDescription}
              onChange={(e) => setWizardDescription(e.target.value)}
              placeholder="What data does this source contain?"
            />
            <DetailLabel>Fields</DetailLabel>
            {wizardFields.map((f) => (
              <div key={f.name} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Badge variant="neutral">{f.name}</Badge>
                <Badge variant="info" size="sm">{f.type}</Badge>
                <Button variant="ghost" onClick={() => removeField(f.name)}>
                  Remove
                </Button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8 }}>
              <Input
                value={newFieldName}
                onChange={(e) => setNewFieldName(e.target.value)}
                placeholder="Field name"
              />
              <StyledSelect
                value={newFieldType}
                onChange={(e) => setNewFieldType(e.target.value)}
              >
                <option value="string">string</option>
                <option value="number">number</option>
                <option value="boolean">boolean</option>
                <option value="date">date</option>
                <option value="text">text</option>
              </StyledSelect>
              <Button variant="secondary" onClick={addField}>
                Add
              </Button>
            </div>
          </StepContent>
        );
      }
      if (wizardStep === 2) {
        return (
          <StepContent>
            <Text variant="h4">AI Field Attributes</Text>
            <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
              Lumina AI will suggest which fields should be searchable or indexable.
            </Text>
            {wizardFields.length > 0 ? (
              <>
                <Table
                  columns={[
                    { key: 'name', header: 'Field' },
                    { key: 'type', header: 'Type' },
                    {
                      key: 'searchable',
                      header: 'Searchable',
                      align: 'center' as const,
                      render: (val: boolean) => (
                        <Badge variant={val ? 'success' : 'neutral'} size="sm">
                          {val ? 'Yes' : 'No'}
                        </Badge>
                      ),
                    },
                    {
                      key: 'indexable',
                      header: 'Indexable',
                      align: 'center' as const,
                      render: (val: boolean) => (
                        <Badge variant={val ? 'success' : 'neutral'} size="sm">
                          {val ? 'Yes' : 'No'}
                        </Badge>
                      ),
                    },
                  ]}
                  data={wizardFields}
                  rowKey="name"
                />
                <Button variant="secondary" onClick={handleAnalyzeSchema}>
                  Analyze with AI
                </Button>
              </>
            ) : (
              <DetailValue style={{ color: 'var(--text-muted)' }}>
                No fields defined. Go back and add fields.
              </DetailValue>
            )}
          </StepContent>
        );
      }
      if (wizardStep === 3) {
        return (
          <StepContent>
            <Text variant="h4">Review &amp; Create</Text>
            <DetailSection>
              <DetailLabel>Name</DetailLabel>
              <DetailValue>{wizardName}</DetailValue>
            </DetailSection>
            <DetailSection>
              <DetailLabel>Type</DetailLabel>
              <Badge variant="info">{DS_TYPE_LABELS[wizardType]}</Badge>
            </DetailSection>
            <DetailSection>
              <DetailLabel>Fields</DetailLabel>
              <DetailValue>{wizardFields.length} field(s) defined</DetailValue>
            </DetailSection>
          </StepContent>
        );
      }
    } else {
      if (wizardStep === 1) {
        return (
          <StepContent>
            <Text variant="h4">Select Provider</Text>
            <SelectWrapper>
              <SelectLabel>Provider</SelectLabel>
              <StyledSelect
                value={wizardProvider}
                onChange={(e) => setWizardProvider(e.target.value)}
              >
                <option value="algolia">Algolia</option>
                <option value="s3" disabled>
                  S3 (coming soon)
                </option>
              </StyledSelect>
            </SelectWrapper>
          </StepContent>
        );
      }
      if (wizardStep === 2) {
        return (
          <StepContent>
            <Text variant="h4">Credentials</Text>
            <Input
              label="Name"
              value={wizardName}
              onChange={(e) => setWizardName(e.target.value)}
              placeholder="e.g. My Algolia Index"
            />
            <Input
              label="Description"
              value={wizardDescription}
              onChange={(e) => setWizardDescription(e.target.value)}
              placeholder="What data does this source contain?"
            />
            <Input
              label="Application ID"
              value={wizardAppId}
              onChange={(e) => setWizardAppId(e.target.value)}
              placeholder="Algolia Application ID"
            />
            <Input
              label="API Key"
              value={wizardApiKey}
              onChange={(e) => setWizardApiKey(e.target.value)}
              placeholder="Algolia Admin API Key"
              type="password"
            />
            <Input
              label="Index Name"
              value={wizardIndexName}
              onChange={(e) => setWizardIndexName(e.target.value)}
              placeholder="e.g. prod_products"
            />
          </StepContent>
        );
      }
      if (wizardStep === 3) {
        return (
          <StepContent>
            <Text variant="h4">Verify Connection</Text>
            <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
              We will create the data source reference and verify the connection
              to your Algolia index.
            </Text>
            <Button
              variant="primary"
              onClick={handleVerifyConnection}
              disabled={isVerifying || isCreating}
            >
              {isVerifying ? 'Verifying...' : 'Verify & Create'}
            </Button>
          </StepContent>
        );
      }
      if (wizardStep === 4) {
        return (
          <StepContent>
            <Text variant="h4">Connection Verified</Text>
            <Badge variant="success">Connected</Badge>
            <Text variant="body1" style={{ marginTop: 8, color: 'var(--text-muted)' }}>
              Your BYO data source has been created and verified.
            </Text>
          </StepContent>
        );
      }
    }
    return null;
  };

  const canGoNext = () => {
    if (wizardStep === 0) return true;
    if (!isByo) {
      if (wizardStep === 1) return wizardName.trim().length > 0;
      if (wizardStep === 2) return true;
      return false;
    } else {
      if (wizardStep === 1) return true;
      if (wizardStep === 2) return wizardName.trim().length > 0 && wizardAppId.trim().length > 0;
      return false;
    }
  };

  const handleNext = () => {
    if (!isByo && wizardStep === 3) {
      handleCreatePlatformManaged();
      return;
    }
    if (isByo && wizardStep === 4) {
      closeWizard();
      return;
    }
    setWizardStep((s) => s + 1);
  };

  /* -------------------------------------------------------------- */
  /*  Schema table columns for detail slide-over                     */
  /* -------------------------------------------------------------- */

  const schemaColumns: Column<SchemaField>[] = [
    { key: 'name', header: 'Field Name' },
    { key: 'type', header: 'Type' },
    {
      key: 'searchable',
      header: 'Searchable',
      align: 'center',
      render: (val: boolean) => (
        <Badge variant={val ? 'success' : 'neutral'} size="sm">
          {val ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      key: 'indexable',
      header: 'Indexable',
      align: 'center',
      render: (val: boolean) => (
        <Badge variant={val ? 'success' : 'neutral'} size="sm">
          {val ? 'Yes' : 'No'}
        </Badge>
      ),
    },
  ];

  /* -------------------------------------------------------------- */
  /*  Render                                                         */
  /* -------------------------------------------------------------- */

  return (
    <PageWrapper>
      <Header>
        <Text variant="h2">Data Sources</Text>
        <Button variant="primary" onClick={openWizard}>
          Add Data Source
        </Button>
      </Header>

      <SearchRow>
        <Input
          placeholder="Search data sources..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, maxWidth: 320 }}
        />
        {(Object.keys(DS_TYPE_LABELS) as DsType[]).map((t) => (
          <FilterBadge
            key={t}
            variant="info"
            size="sm"
            $active={typeFilter === t}
            onClick={() => setTypeFilter(typeFilter === t ? null : t)}
          >
            {DS_TYPE_LABELS[t]}
          </FilterBadge>
        ))}
      </SearchRow>

      {error && (
        <ErrorBanner>
          Failed to load data sources. Please try again.
        </ErrorBanner>
      )}

      {isLoading ? (
        <EmptyState>Loading data sources...</EmptyState>
      ) : filteredSources.length === 0 ? (
        <EmptyState>
          {dataSources?.length === 0
            ? 'No data sources yet. Click "Add Data Source" to get started.'
            : 'No data sources match your search.'}
        </EmptyState>
      ) : (
        <Grid>
          {filteredSources.map((ds) => (
            <DSCard
              key={ds.dataSourceId}
              padding={20}
              elevation="sm"
              onClick={() => setSelected(ds)}
            >
              <CardTop>
                <Text variant="h4">{ds.name}</Text>
                <Badge variant="info" size="sm">
                  {DS_TYPE_LABELS[ds.dsType] ?? ds.dsType}
                </Badge>
              </CardTop>
              {ds.description && (
                <Text
                  variant="body1"
                  style={{
                    color: 'var(--text-muted)',
                    fontSize: '0.8125rem',
                    marginTop: 4,
                  }}
                >
                  {ds.description}
                </Text>
              )}
              <CardMeta>
                <MetaItem>
                  {(ds.recordCount ?? 0).toLocaleString()} records
                </MetaItem>
                <MetaItem>
                  <Badge variant="neutral" size="sm">
                    {(ds.agentIds?.length ?? 0)} agent
                    {(ds.agentIds?.length ?? 0) !== 1 ? 's' : ''}
                  </Badge>
                </MetaItem>
              </CardMeta>
            </DSCard>
          ))}
        </Grid>
      )}

      {/* Detail SlideOver */}
      <SlideOver
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name ?? ''}
        width="lg"
        position="right"
      >
        {selected && (
          <>
            <DetailSection>
              <DetailLabel>Data Source ID</DetailLabel>
              <DetailValue style={{ fontFamily: 'monospace' }}>
                {selected.dataSourceId}
              </DetailValue>
            </DetailSection>

            <DetailSection>
              <DetailLabel>Type</DetailLabel>
              <Badge variant="info">
                {DS_TYPE_LABELS[selected.dsType] ?? selected.dsType}
              </Badge>
              {selected.provider && (
                <Badge variant="neutral" style={{ marginLeft: 8 }}>
                  {selected.provider}
                </Badge>
              )}
            </DetailSection>

            {selected.description && (
              <DetailSection>
                <DetailLabel>Description</DetailLabel>
                <DetailValue>{selected.description}</DetailValue>
              </DetailSection>
            )}

            <DetailSection>
              <DetailLabel>Records</DetailLabel>
              <DetailValue>
                {(selected.recordCount ?? 0).toLocaleString()}
              </DetailValue>
            </DetailSection>

            <DetailSection>
              <DetailLabel>Status</DetailLabel>
              <Badge
                variant={selected.status === 'active' ? 'success' : 'warning'}
              >
                {selected.status}
              </Badge>
            </DetailSection>

            <Divider />

            <DetailSection>
              <DetailLabel>Schema</DetailLabel>
              {selected.schemaDefinition?.fields?.length ? (
                <Table
                  columns={schemaColumns}
                  data={selected.schemaDefinition.fields}
                  rowKey="name"
                />
              ) : (
                <DetailValue style={{ color: 'var(--text-muted)' }}>
                  No schema fields defined
                </DetailValue>
              )}
            </DetailSection>

            <Divider />

            <DetailSection>
              <DetailLabel>Partner</DetailLabel>
              <DetailValue>
                {selected.partnerName} ({selected.partnerId})
              </DetailValue>
            </DetailSection>

            <DetailSection>
              <DetailLabel>Agent Associations</DetailLabel>
              {(selected.agentIds?.length ?? 0) === 0 ? (
                <DetailValue style={{ color: 'var(--text-muted)' }}>
                  No agents associated
                </DetailValue>
              ) : (
                <DetailValue>
                  {selected.agentIds.length} agent(s) associated
                </DetailValue>
              )}
            </DetailSection>

            <Divider />

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {deleteConfirm === selected.dataSourceId ? (
                <>
                  <Text
                    variant="body1"
                    style={{ color: 'var(--danger)', marginRight: 12, alignSelf: 'center' }}
                  >
                    Are you sure?
                  </Text>
                  <Button
                    variant="ghost"
                    onClick={() => setDeleteConfirm(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleDelete(selected.dataSourceId)}
                    style={{ background: 'var(--danger)', marginLeft: 8 }}
                  >
                    Delete
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  onClick={() => setDeleteConfirm(selected.dataSourceId)}
                  style={{ color: 'var(--danger)' }}
                >
                  Delete Data Source
                </Button>
              )}
            </div>
          </>
        )}
      </SlideOver>

      {/* Create Wizard SlideOver */}
      <SlideOver
        isOpen={wizardOpen}
        onClose={closeWizard}
        title={`New Data Source — Step ${wizardStep + 1} of ${totalSteps}`}
        width="lg"
        position="right"
      >
        {wizardError && <ErrorBanner>{wizardError}</ErrorBanner>}

        {renderWizardStep()}

        <WizardActions>
          {wizardStep > 0 && !(isByo && wizardStep === 4) && (
            <Button variant="ghost" onClick={() => setWizardStep((s) => s - 1)}>
              Back
            </Button>
          )}
          <Button variant="ghost" onClick={closeWizard}>
            Cancel
          </Button>
          {!(isByo && wizardStep === 3) && (
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={!canGoNext() || isCreating}
            >
              {!isByo && wizardStep === 3
                ? isCreating
                  ? 'Creating...'
                  : 'Create'
                : isByo && wizardStep === 4
                  ? 'Done'
                  : 'Next'}
            </Button>
          )}
        </WizardActions>
      </SlideOver>
    </PageWrapper>
  );
};

export default DataSources;
